import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Star, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [checking, setChecking] = useState(true);
  const [payment, setPayment] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPayment(response.data);
        setChecking(false);
        toast.success('Payment successful!');
        
        // Refresh user data to get updated gold membership status
        if (response.data.item_type === 'gold_membership') {
          await refreshUser();
        }
      } else if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          pollPaymentStatus();
        }, 2000);
      } else {
        setChecking(false);
        toast.error('Payment verification timed out. Please check your email.');
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          pollPaymentStatus();
        }, 2000);
      } else {
        setChecking(false);
        toast.error('Failed to verify payment');
      }
    }
  };

  const isGoldMembership = payment?.item_type === 'gold_membership';

  return (
    <ConsumerLayout hideFooter>
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Card data-testid="payment-success-card">
          <CardContent className="p-12 text-center">
            {checking ? (
              <>
                <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-foreground mb-4">
                  Verifying Payment...
                </h1>
                <p className="text-muted-foreground">
                  Please wait while we confirm your payment
                </p>
              </>
            ) : payment ? (
              isGoldMembership ? (
                // Gold Club Welcome
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                    <Star className="w-10 h-10 text-amber-900" fill="currentColor" />
                  </div>
                  <h1 className="text-3xl font-bold text-foreground mb-4">
                    Welcome to Gold Club!
                  </h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    You're now part of Denver's most exclusive nightlife community. Your benefits are active immediately.
                  </p>
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 rounded-lg p-6 mb-8 text-left border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-4">
                      <Crown className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800 dark:text-amber-400">Your Gold Benefits</span>
                    </div>
                    <ul className="space-y-2 text-sm text-amber-700 dark:text-amber-300">
                      <li>✓ Up to 25% off on experiences and events</li>
                      <li>✓ Priority access to sold-out events</li>
                      <li>✓ Exclusive members-only experiences</li>
                      <li>✓ VIP concierge from Evis AI</li>
                    </ul>
                  </div>
                  <div className="flex gap-4 justify-center">
                    <Button
                      onClick={() => navigate('/gold-club/dashboard?welcome=true')}
                      className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-amber-900"
                      data-testid="explore-gold-btn"
                    >
                      <Star className="w-4 h-4 mr-2" fill="currentColor" />
                      Explore Gold Benefits
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/marketplace')}
                      data-testid="browse-experiences-btn"
                    >
                      Browse Experiences
                    </Button>
                  </div>
                </>
              ) : (
                // Regular purchase success
                <>
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
                  <h1 className="text-3xl font-bold text-foreground mb-4">
                    Payment Successful!
                  </h1>
                  <p className="text-lg text-muted-foreground mb-8">
                    Thank you for your purchase. Your booking has been confirmed.
                  </p>
                  <div className="bg-muted rounded-lg p-6 mb-8 text-left">
                    <div className="flex justify-between mb-2">
                      <span className="text-muted-foreground">Amount Paid:</span>
                      <span className="font-bold">${payment.amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Transaction ID:</span>
                      <span className="font-mono text-sm">{payment.session_id.substring(0, 20)}...</span>
                    </div>
                  </div>
                  <div className="flex gap-4 justify-center">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="view-bookings-btn"
                    >
                      View My Bookings
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/events')}
                      data-testid="browse-more-events-btn"
                    >
                      Browse More Events
                    </Button>
                  </div>
                </>
              )
            ) : (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-4">
                  Payment Verification
                </h1>
                <p className="text-muted-foreground mb-8">
                  We couldn't verify your payment at this time. Please check your email for confirmation or contact support.
                </p>
                <Button onClick={() => navigate('/')} data-testid="back-home-btn">
                  Back to Home
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </ConsumerLayout>
  );
};

export default PaymentSuccess;
